import React, { useRef, useEffect } from "react";
import { translate } from "../Services/translation";

const Dialog = ({
  isOpen,
  onClose,
  children,
  headerContent,
  closeButtonAriaLabel = translate("general.close"),
}) => {
  const dialogRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    document.body.classList.toggle("dialog-open", isOpen);
    document.documentElement.classList.toggle("dialog-open", isOpen);

    return () => {
      document.body.classList.toggle("dialog-open", false);
      document.documentElement.classList.toggle("dialog-open", false);
    };
  }, [isOpen]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen) {
      dialog.showModal();
    } else {
      dialog.close();
    }

    const focusableElements = dialog.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose && onClose();
      }
      if (event.key === "Tab" && firstFocusable && lastFocusable) {
        if (event.shiftKey) {
          if (document.activeElement === firstFocusable) {
            lastFocusable.focus();
            event.preventDefault();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            firstFocusable.focus();
            event.preventDefault();
          }
        }
      }
    };

    const handleOutsideClick = (event) => {
      if (contentRef.current && !contentRef.current.contains(event.target)) {
        onClose && onClose();
      }
    };

    dialog.addEventListener("keydown", handleKeyDown);
    dialog.addEventListener("click", handleOutsideClick);

    return () => {
      dialog.removeEventListener("keydown", handleKeyDown);
      dialog.removeEventListener("click", handleOutsideClick);
    };
  }, [isOpen, onClose]);

  return (
    <dialog className="dialog" ref={dialogRef} onClose={onClose}>
      <div className="dialog__inner" ref={contentRef}>
        <div className="dialog__header">
          {headerContent && (
            <div className="dialog__header-content">{headerContent}</div>
          )}
          <button
            className="dialog__close"
            aria-label={closeButtonAriaLabel}
            onClick={onClose}
          >
            &#x2715;
          </button>
        </div>
        <div className="dialog__content">{children}</div>
      </div>
    </dialog>
  );
};

export default Dialog;
